import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import JSZip from 'jszip';
import {environment} from '../../../environments/environment';

export interface FlashlightAnimation {
  id: number;
  key: string;
  name: string;
  fps: number;
  is_pattern: boolean;
  preview_only: boolean;
  image_url: string;
}

interface ManifestEntry {
  id: number;
  image: string;
  name: string;
  fps: number;
  isPattern: boolean;
  previewOnly?: boolean;
}

@Injectable({providedIn: 'root'})
export class AnimationsService {
  constructor(private http: HttpClient) {
  }

  getAnimations(): Observable<FlashlightAnimation[]> {
    return this.http.get(environment.mobileApiUrl + '/api/mobile/flashlight/animations', {responseType: 'arraybuffer'}).pipe(
      switchMap(content => this.readArchive(content))
    );
  }

  private async readArchive(content: ArrayBuffer): Promise<FlashlightAnimation[]> {
    const archive = await JSZip.loadAsync(content);
    const manifestFile = archive.file('animatios_id.json');
    if (!manifestFile) {
      throw new Error('Animation manifest is missing.');
    }

    const manifest: Record<string, ManifestEntry> = JSON.parse(await manifestFile.async('string'));
    if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) {
      throw new Error('Invalid animation manifest.');
    }

    const animations = await Promise.all(Object.entries(manifest).map(async ([key, entry]) => {
      if (!entry || !Number.isInteger(entry.id) || typeof entry.name !== 'string'
        || !Number.isInteger(entry.fps) || entry.fps <= 0 || typeof entry.isPattern !== 'boolean'
        || typeof entry.image !== 'string' || !entry.image.endsWith('.png')
        || /[\\/]/.test(entry.image)
        || (entry.previewOnly !== undefined && typeof entry.previewOnly !== 'boolean')) {
        throw new Error(`Invalid animation '${key}'.`);
      }

      const image = archive.file(entry.image);
      if (!image) {
        throw new Error(`Animation image '${entry.image}' is missing.`);
      }

      return {
        id: entry.id,
        key,
        name: entry.name,
        fps: entry.fps,
        is_pattern: entry.isPattern,
        preview_only: entry.previewOnly ?? false,
        image_url: 'data:image/png;base64,' + await image.async('base64')
      };
    }));

    return animations.sort((left, right) => left.id - right.id);
  }
}
